'use client'

import { useEffect } from 'react'
import { UserStatusEnum } from 'types/subscription'

import { API_ROUTES, AUTH_TOKEN_COOKIE, ROUTES } from '@/constants'
import { useRouter } from '@/i18n-routing'
import { getCookieClient } from '@/utils/client'

import { User } from '../../../../types/auth'

/**
 * Covers client-side redirection to dashboard for authenticated users on statically rendered landing pages, including bfcache restores.
 * Middleware refreshes an expired access token before the page is served, so a missing access token cookie means there is no session to check.
 */
export const UseEnsureAthenticatedUserRedirectedToDashboard = () => {
	const router = useRouter()

	useEffect(() => {
		const check = async () => {
			if (!getCookieClient(AUTH_TOKEN_COOKIE)) return

			try {
				const { getApiClient } = await import('@/api')
				const { data: user } = await getApiClient().get<User>(
					API_ROUTES.userSettings.getUser,
					{ withCredentials: true },
				)
				const url =
					user.status === UserStatusEnum.NEVER_PAID
						? ROUTES.checkout.index
						: ROUTES.cabinet.qrCodes
				router.replace(url, { locale: user.language })
			} catch {
				// Do nothing if user is not authenticated
			}
		}

		check()

		const onPageShow = (e: PageTransitionEvent) => {
			if (e.persisted) void check()
		}
		window.addEventListener('pageshow', onPageShow)
		return () => window.removeEventListener('pageshow', onPageShow)
	}, [router])

	return null
}
